import React from "react";
import { Heart, Home, Briefcase, Music2, Mic2, Lightbulb, ListMusic, MapPin, Armchair, Users, UtensilsCrossed } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/seo/Seo";
import JsonLd from "../components/seo/JsonLd";
import HostForm from "../components/forms/HostForm";
import { Reveal, Eyebrow, LotusDivider } from "../components/Decorative";
import { ROUTES, breadcrumbs } from "../lib/seo";
import { byFile, src as photoSrc, srcSet as photoSrcSet, webpSrcSet } from "../lib/gallery";

/**
 * Host Antara — the site's first ask now that nothing is on sale: Antara at
 * someone's own occasion. The page makes the case (what the evening looks
 * like at a wedding, at home, at a corporate evening; what we bring and what
 * the host arranges; the room singing) and then asks, with Form B. The form
 * still posts to the Netlify form "private-event" (lib/forms.js).
 */
export const HOST_FORM_ANCHOR = "enquire";

const OCCASIONS = [
  {
    icon: Heart,
    title: "A wedding function or sangeet",
    body:
      "Before the dancing, or instead of it. Both families in one hall or on one lawn, the band holding the groove, Anshu drawing both sides into the same refrains. By the third bhajan the aunts are on their feet and the cousins have stopped filming and started singing. It becomes the part of the wedding people tell each other about.",
  },
  {
    icon: Home,
    title: "A gathering at home",
    body:
      "A housewarming, a birthday, an anniversary, the evening after a griha pravesh. Forty people in a living room or two hundred on a terrace: the band scales to the room, the volume stays kind to the neighbours, and the evening moves from quiet bhajans to full voices and back to stillness.",
  },
  {
    icon: Briefcase,
    title: "A corporate evening",
    body:
      "An offsite, a Diwali evening, a founders' dinner, a temple or community gathering. No spectators: the whole room sings the same lines, whatever their desk or their age. Two hours that leave people lighter than any team-building exercise we know of.",
  },
];

const WE_BRING = [
  { icon: Mic2, text: "Anshu Goyal, her stories and the bhajans" },
  { icon: Music2, text: "The Antara band: vocals, guitar, keys, tabla and percussion" },
  { icon: Lightbulb, text: "Sound and lights sized to the room, or we work with your vendor" },
  { icon: ListMusic, text: "A set shaped to your occasion, from the quiet opening to the closing stillness" },
];

const YOU_ARRANGE = [
  { icon: MapPin, text: "The room: a lawn, a hall, a terrace, a living room" },
  { icon: Armchair, text: "Seating that lets people stand up when they want to" },
  { icon: Users, text: "Your guests, and the date" },
  { icon: UtensilsCrossed, text: "Dinner, if there is one, after the last refrain" },
];

// Three rooms singing, from the gallery manifest: never empty seats.
const PHOTOS = [
  byFile("chandigarh-2026-10-045"),
  byFile("delhi-2026-10-034"),
  byFile("delhi-2026-08-016"),
].filter(Boolean);

export default function HostAntara() {
  return (
    <>
      <Seo route={ROUTES.host} />
      <JsonLd data={breadcrumbs([{ name: ROUTES.host.crumb, path: ROUTES.host.path }])} />

      <PageHero
        title={
          <>
            Host <span className="gold-grad italic">Antara</span>
            <span className="block mt-3 font-serif-italic font-normal tracking-normal" style={{ color: "#E5C880", fontSize: "0.5em" }}>
              for your occasion
            </span>
          </>
        }
        intro="A live band, Anshu Goyal's stories, and a room of your own people singing together: a wedding function, a sangeet, a housewarming, a corporate evening, a temple gathering. Tell us about the occasion and we'll shape the evening around it."
      >
        <div className="mt-8 flex flex-col items-center">
          <a href={`#${HOST_FORM_ANCHOR}`} className="cta-primary" data-testid="host-enquire-btn">
            <span>Tell us about your occasion</span>
            <span className="cta-arrow">→</span>
          </a>
          <p className="mt-3 font-body text-sm" style={{ color: "#E8D9C5", opacity: 0.8 }}>
            Two minutes to fill in. We read every one ourselves and call you back.
          </p>
        </div>
      </PageHero>

      {/* The occasions */}
      <section className="bg-cream py-20 md:py-28 px-6" data-testid="host-occasions">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <Eyebrow>Where it happens</Eyebrow>
              <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink leading-[1.08]">
                The same evening, <span className="gold-grad italic">in your room</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5 md:gap-6">
            {OCCASIONS.map((o, i) => (
              <Reveal key={o.title} delay={i * 0.08} className="h-full">
                <div className="h-full rounded-2xl border border-gold/25 bg-ivory-2 p-7 md:p-8">
                  <span className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#E5C880] to-[#B8923E]">
                    <o.icon size={22} strokeWidth={1.4} color="#2A1F17" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-display text-2xl md:text-[26px] leading-tight text-ink">{o.title}</h3>
                  <p className="mt-3 font-body text-[15px] leading-relaxed text-ink-2/85">{o.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* The room singing */}
      {PHOTOS.length > 0 && (
        <section className="bg-[#0F0A07] py-6 md:py-8 px-3 md:px-6" data-testid="host-photos">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {PHOTOS.map((p, i) => (
              <Reveal key={p.file} delay={i * 0.08}>
                <div className="aspect-[4/3] overflow-hidden rounded-2xl border" style={{ borderColor: "rgba(201,169,97,0.25)" }}>
                  <picture>
                    <source type="image/webp" srcSet={webpSrcSet(p)} sizes="(min-width: 768px) 33vw, 100vw" />
                    <img
                      src={photoSrc(p, 1200)}
                      srcSet={photoSrcSet(p, "jpg")}
                      sizes="(min-width: 768px) 33vw, 100vw"
                      alt={p.alt}
                      width={p.width}
                      height={p.height}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                      style={{ backgroundImage: `url(${p.lqip})`, backgroundSize: "cover" }}
                    />
                  </picture>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* What we bring, what you arrange */}
      <section className="bg-[#0F0A07] py-20 md:py-28 px-6" data-testid="host-brings">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <Eyebrow>How it comes together</Eyebrow>
              <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.08]" style={{ color: "#FBF6EE" }}>
                We bring the evening. <span className="gold-grad italic">You bring the room.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-5 md:gap-6">
            {[
              { title: "What Antara brings", items: WE_BRING },
              { title: "What you arrange", items: YOU_ARRANGE },
            ].map((col, i) => (
              <Reveal key={col.title} delay={i * 0.1} className="h-full">
                <div className="h-full rounded-2xl border p-7 md:p-8" style={{ background: "#1C1A17", borderColor: "rgba(201,169,97,0.3)" }}>
                  <h3 className="font-display text-2xl md:text-[28px]" style={{ color: "#FBF6EE" }}>{col.title}</h3>
                  <ul className="mt-5 space-y-4">
                    {col.items.map((it) => (
                      <li key={it.text} className="flex items-start gap-3 font-body text-[15px] leading-relaxed" style={{ color: "#E8D9C5" }}>
                        <it.icon size={18} strokeWidth={1.4} className="mt-0.5 flex-shrink-0" style={{ color: "#C9A961" }} aria-hidden="true" />
                        <span>{it.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-2xl mx-auto text-center font-body text-base md:text-lg leading-relaxed" style={{ color: "#E8D9C5" }}>
              Tell us the occasion, the city and a date if you have one. We call you back to talk it
              through, then send a proposal and hold the date.
            </p>
          </Reveal>
          <LotusDivider />
        </div>
      </section>

      {/* The ask */}
      <section id={HOST_FORM_ANCHOR} className="scroll-mt-20 bg-[#120C08] pt-16 pb-24 md:pt-20 md:pb-32 px-5" data-testid="private-event-section">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <Eyebrow>Host Antara</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.08]" style={{ color: "#FBF6EE" }}>
              Tell us about <span className="gold-grad italic">your occasion</span>
            </h2>
            <p className="mt-4 font-body text-base" style={{ color: "#E8D9C5" }}>
              Only the occasion and a way to reach you are needed. Everything else helps us shape the evening.
            </p>
          </div>
        </Reveal>
        <HostForm tone="dark" />
      </section>
    </>
  );
}
